import { HttpRequest } from "../utils";

const httpRequest = new HttpRequest();

export interface ComponentInput {
  componentType: "RECURRING_SUBSCRIPTION" | "ONE_TIME_PURCHASE" | "TRANSACTION_FEE" | "FREE_OR_CUSTOM" | "END_CUSTOMER_RECURRING";
  isEnabled?: boolean;
  label?: string;
  description?: string;
  currency?: string;
  recurringAmount?: string;
  recurringFrequency?: "MONTHLY" | "QUARTERLY" | "ANNUAL";
  recurringAnchorDay?: number;
  recurringStartsAt?: string | null;
  recurringEndsAt?: string | null;
  oneTimeAmount?: string;
  oneTimeChargeableAfter?: string | null;
  oneTimeLicenseExpiresAt?: string | null;
  txFeeRate?: string;
  txFeeMinAmount?: string;
  txFeeMaxAmount?: string;
  txFeeApplicableOrderTypes?: string[];
  customTerms?: string;
  customExpiresAt?: string | null;
  // END_CUSTOMER_RECURRING fields
  ecRecurringAmount?: string | null;
  ecRecurringFrequency?: string | null;
  ecRecurringAnchorDay?: number | null;
  ecCollectionMode?: string | null;
  ecDelinquencyAction?: string | null;
  ecApplicableCustomerTypes?: string[];
  ecExemptedCustomerIds?: string[];
  /** Server-computed live count of eligible end-customers (END_CUSTOMER_RECURRING only). */
  ecActiveMerchantCount?: number;
}

export interface ChargeContract {
  id: string;
  tenantId: string;
  contractNumber: string;
  contractName: string;
  status: "DRAFT" | "ACTIVE" | "SUSPENDED" | "TERMINATED" | "ARCHIVED";
  rowVersion: number;
  effectiveFrom: string;
  effectiveUntil: string | null;
  currency: string;
  negotiatedBy: string | null;
  signedDocumentUrl: string | null;
  components: Array<ComponentInput & { id: string }>;
  createdAt: string;
  updatedAt: string;
  terminatedAt: string | null;
  terminationReason: string | null;
}

export interface ListParams {
  tenantId?: string;
  status?: ChargeContract["status"];
  page?: number;
  limit?: number;
}

export const list = (params?: ListParams) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.get("/bil/admin/charge-contracts", params as any);

export const get = (id: string) =>
  httpRequest.get(`/bil/admin/charge-contracts/${id}`);

export const create = (body: {
  tenantId: string;
  contractName: string;
  currency: string;
  effectiveFrom: string;
  effectiveUntil?: string | null;
  negotiatedBy?: string;
  signedDocumentUrl?: string;
  components: ComponentInput[];
// eslint-disable-next-line @typescript-eslint/no-explicit-any
}) => httpRequest.post("/bil/admin/charge-contracts", body as any);

export const update = (id: string, body: {
  rowVersion: number;
  contractName?: string;
  effectiveFrom?: string;
  effectiveUntil?: string | null;
  negotiatedBy?: string;
  signedDocumentUrl?: string;
  components: ComponentInput[];
// eslint-disable-next-line @typescript-eslint/no-explicit-any
}) => httpRequest.put(`/bil/admin/charge-contracts/${id}`, body as any);

export const activate = (id: string, rowVersion: number) =>
  httpRequest.post(`/bil/admin/charge-contracts/${id}/activate`, { rowVersion });

export const suspend = (id: string, rowVersion: number) =>
  httpRequest.post(`/bil/admin/charge-contracts/${id}/suspend`, { rowVersion });

export const resume = (id: string, rowVersion: number) =>
  httpRequest.post(`/bil/admin/charge-contracts/${id}/resume`, { rowVersion });

export const terminate = (id: string, rowVersion: number, reason: string) =>
  httpRequest.post(`/bil/admin/charge-contracts/${id}/terminate`, { rowVersion, reason });

export const cancel = (id: string, rowVersion: number) =>
  httpRequest.post(`/bil/admin/charge-contracts/${id}/cancel`, { rowVersion });

// Count of a tenant's active end-customers matching the given customer types,
// minus exemptions. Backend parses `types`/`exempted` as comma-separated strings.
export const endCustomerCount = (
  tenantId: string,
  params: { types: string[]; exempted?: string[] }
): Promise<{ count: number }> =>
  httpRequest.get(`/bil/admin/tenants/${tenantId}/end-customer-count`, {
    types   : params.types.join(","),
    exempted: (params.exempted ?? []).join(",")
  });
